import update from 'react-addons-update';
import {
  COMMENT_CREATE_API_PENDING,
  COMMENT_CREATE_API_REJECTED,
  ISSUE_CREATE_API_FULFILLED,
  ISSUE_CREATE_API_PENDING,
  ISSUE_CREATE_API_REJECTED,
  ISSUE_INSTANCE_FETCH_API_PENDING,
  ISSUE_INSTANCE_FETCH_API_REJECTED,
  ISSUE_INSTANCE_FETCH_PAYLOAD_INJECT,
  ISSUE_UPDATE_API_FULFILLED,
  ISSUE_UPDATE_API_PENDING,
  ISSUE_UPDATE_API_REJECTED,
  ISSUES_CLEAR_SEARCH,
  ISSUES_COLLECTION_RESET,
  ISSUES_FETCH_API_PENDING,
  ISSUES_FETCH_API_REJECTED,
  ISSUES_FETCH_END,
  ISSUES_FETCH_LOADING_START,
  ISSUES_FETCH_PAYLOAD_INJECT,
  ISSUES_FETCH_SEARCH_API_FULFILLED,
  REMOVE_ISSUE_FROM_COLLECTION
} from '../actions';
import {isBadPermissionStatus} from '../../utils/jsonapi-helpers';
import lodashGet from 'lodash/get';
import {TYPE_QUALITY_ISSUES} from '../../constants/issue-types';
import {ISSUE_ATTRIBUTE_NAMES} from '../../constants/issue-attribute-names';
import * as Immutable from 'seamless-immutable';

const initialChannelItems = {
  loading: false,
  error: null,
  sortOrder: [],
  collection: {}
};

// Shared by Issues Collection and Issue instances:
const injectChannelItems = (issuesState, issueId, commentProps) => {
  // channel-items were, at one point, separated by type.  They are now consolidated under a single 'channelItems'
  // property, in each issue-wrapper -- this could be reverted to multiple collections, via the 1st argument:
  if (lodashGet(issuesState[issueId], 'channelItems')) {
    // eslint-disable-next-line no-use-before-define
    return injectItems('channelItems', issuesState, issueId, commentProps);
  }
  // eslint-disable-next-line no-use-before-define
  return injectItems(
    'channelItems',
    update(issuesState, {[issueId]: {$merge: {channelItems: initialChannelItems}}}),
    issueId,
    commentProps
  );
};

const sortByCreatedAt = (a, b) => {
  const aValue = a.isDeleteAttachmentChange ? a.deletedAt : a.createdAt;
  const bValue = b.isDeleteAttachmentChange ? b.deletedAt : b.createdAt;
  return aValue === bValue ? 0 : aValue > bValue ? 1 : -1;
};

const injectIssue = (issuesState, issueWrapper) => {
  const id = issueWrapper.id;
  delete issueWrapper.id;
  const targetIssueWrapper = issuesState[id];
  const changeToStatus = issueWrapper.changeToStatus;
  const command = targetIssueWrapper ? '$merge' : '$set';

  // We don't want to "loose" any attributes that already exist (in case the issue was fetched as an instance before
  // it was fetched as part of a collection) so we need to merge attributes first and return the updated state after
  if (targetIssueWrapper && targetIssueWrapper.attributes && issueWrapper.attributes) {
    issueWrapper.attributes = update(targetIssueWrapper.attributes, {
      $merge: issueWrapper.attributes
    });
  }

  //changeToStatus is bean send by transition to the status
  if (changeToStatus) {
    issueWrapper.attributes.changeToStatus = changeToStatus;
  }

  return update(issuesState, {[id]: {[command]: issueWrapper}});
};

const addDeletedAttachmentsChanges = itemProps => {
  if (itemProps.collection) {
    const collection = {};
    Object.keys(itemProps.collection).forEach(k => {
      const item = itemProps.collection[k];
      collection[k] = item;
      // When getting an attachment with `deletedAt` - we need to create additional event indicating attachment deletion.
      if (item.type === 'attachments' && item.deletedAt) {
        const deleteId = `${k}-delete`;
        collection[deleteId] = {...item, id: deleteId, isDeleteAttachmentChange: true};
      }
    });
    return {...itemProps, collection};
  }
  return itemProps;
};

const injectItems = (collectionName, issuesState, issueId, itemProps) => {
  const targetIssueWrapper = issuesState[issueId];
  const transformedItemProps = addDeletedAttachmentsChanges(itemProps);
  if (targetIssueWrapper) {
    const updatedItems = update(targetIssueWrapper[collectionName], {
      $merge: transformedItemProps
    });
    const itemsArray = Object.keys(updatedItems.collection).map(key => updatedItems.collection[key]);
    updatedItems.sortOrder = itemsArray.sort(sortByCreatedAt).map(channelItem => channelItem.id);
    return update(issuesState, {
      [issueId]: {
        $merge: update(targetIssueWrapper, {$merge: {[collectionName]: updatedItems}})
      }
    });
  } else {
    // items are not injected, if current issue id is not in issuesState:
    return issuesState;
  }
};

// Issue Collection:
const issuesLoadingReducer = (state = false, action) => {
  switch (action.type) {
    case ISSUES_FETCH_API_PENDING:
      return true;
    case ISSUES_FETCH_PAYLOAD_INJECT:
    case ISSUES_FETCH_API_REJECTED:
      return false;
    default:
      return state;
  }
};

const issuesFetchedOnceReducer = (state = false, action) => {
  switch (action.type) {
    case ISSUES_FETCH_PAYLOAD_INJECT:
    case ISSUES_FETCH_API_REJECTED:
      return true;
    default:
      return state;
  }
};

const issuesErrorReducer = (state = null, action) => {
  switch (action.type) {
    case ISSUES_FETCH_API_PENDING:
    case ISSUES_FETCH_PAYLOAD_INJECT:
      return null;
    case ISSUES_FETCH_API_REJECTED:
      return action.payload;
    default:
      return state;
  }
};

const lastUpdatedKeyReducer = (state = '', action) => {
  switch (action.type) {
    case ISSUE_UPDATE_API_FULFILLED:
    case ISSUE_CREATE_API_FULFILLED:
      return ['id', 'updatedAt'].map(attributeName => action.payload[attributeName]).join('-');
    default:
      return state;
  }
};

const issuesLastQueryKeyReducer = (state = {}, action) => {
  switch (action.type) {
    case ISSUES_FETCH_API_PENDING: {
      const {
        meta: {
          query: {offset},
          queryKey
        }
      } = action;
      if (offset === 0) {
        return update(state, {[TYPE_QUALITY_ISSUES]: {$set: queryKey}});
      }
      return state;
    }
    default:
      return state;
  }
};

const issuesSortOrderReducer = (state = [], action) => {
  switch (action.type) {
    case ISSUES_COLLECTION_RESET: {
      // TODO: Only reset when jsonapi query has changed (ignoring offset)
      // naive implementation -- always reset:

      const {currentlyViewedId} = action.payload;
      // Remove issuesIds by issue type (also leave issuesIds that are not yet fulfilled)
      return state.filter(issueId => currentlyViewedId === issueId);
    }
    case ISSUES_FETCH_PAYLOAD_INJECT:
      // For internal views, use the default order from the jsonapi responses:
      return state.slice(0).concat(action.payload.map(({id}) => id));
    case ISSUE_CREATE_API_FULFILLED:
      return update(state, {$unshift: [action.payload.id]});
    default:
      return state;
  }
};

const issuesBatchLoadingReducer = (state = false, action) => {
  switch (action.type) {
    case ISSUES_FETCH_LOADING_START:
      return true;
    case ISSUES_FETCH_END:
      return false;
    default:
      return state;
  }
};

const issuesCollectionReducer = (issuesState = {}, action) => {
  switch (action.type) {
    case ISSUES_COLLECTION_RESET: {
      // Remove issues from state by issue type
      const updatedCollection = {};
      const {currentlyViewedId} = action.payload;
      // eslint-disable-next-line no-restricted-syntax
      for (const issueId in issuesState) {
        // Remove issuesIds by issue type (also leave issues that are not yet fulfilled)
        if (currentlyViewedId === issueId) {
          updatedCollection[issueId] = issuesState[issueId];
        }
      }

      return updatedCollection;
    }
    case ISSUES_FETCH_PAYLOAD_INJECT:
      return action.payload.reduce(
        (map, attributes) =>
          // inject one issue at a time:
          injectIssue(map, {
            id: attributes.id,
            loading: false,
            updating: false,
            error: null,
            attributes
          }),
        issuesState
      );
    case ISSUE_INSTANCE_FETCH_PAYLOAD_INJECT:
      return injectIssue(issuesState, {
        id: action.meta.id,
        loading: false,
        updating: false,
        error: null,
        attributes: action.payload
      });
    case ISSUE_INSTANCE_FETCH_API_PENDING:
      return injectIssue(issuesState, {
        id: action.meta.id,
        loading: true,
        updating: false,
        error: null
      });
    case ISSUE_INSTANCE_FETCH_API_REJECTED: {
      if (isBadPermissionStatus(action.payload.response.status)) {
        const newIssuesState = Object.assign({}, issuesState);
        delete newIssuesState[action.meta.id];
        return newIssuesState;
      }
      return injectIssue(issuesState, {
        id: action.meta.id,
        loading: false,
        updating: false,
        error: action.payload
      });
    }
    case ISSUE_UPDATE_API_PENDING:
      return injectIssue(issuesState, {
        id: action.meta.id,
        updating: true,
        error: null
      });
    case ISSUE_UPDATE_API_REJECTED:
      return injectIssue(issuesState, {
        id: action.meta.id,
        updating: false,
        error: action.payload
      });
    case ISSUE_UPDATE_API_FULFILLED:
      return injectIssue(issuesState, {
        id: action.meta.id,
        changeToStatus: lodashGet(action.meta, 'changeToStatus'),
        updating: false,
        error: null,
        attributes: action.payload
      });
    case REMOVE_ISSUE_FROM_COLLECTION: {
      const newIssuesState = Object.assign({}, issuesState);
      delete newIssuesState[action.payload];

      return newIssuesState;
    }
    case ISSUE_CREATE_API_FULFILLED:
      return injectIssue(issuesState, {
        id: action.payload.id,
        updating: false,
        loading: false,
        error: null,
        attributes: action.payload,
        // INITIAL CHANNEL STATE:
        channelItems: initialChannelItems
      });
    case ISSUE_CREATE_API_PENDING:
    case ISSUE_CREATE_API_REJECTED:
      return issuesState;
    case COMMENT_CREATE_API_PENDING:
      return injectChannelItems(issuesState, action.meta.id, {
        loading: true,
        error: null
      });
    case COMMENT_CREATE_API_REJECTED:
      return injectChannelItems(issuesState, action.meta.id, {
        loading: false,
        error: action.payload
      });
    default:
      return issuesState;
  }
};
const paginationInitialState = Immutable.from({limit: 0, totalResults: 0, offset: 0});

const issuesCollectionPaginationReducer = (state = paginationInitialState, action) => {
  switch (action.type) {
    case ISSUES_FETCH_PAYLOAD_INJECT:
      return state.merge(action.payload[0]?.[ISSUE_ATTRIBUTE_NAMES.PAGINATION] ?? paginationInitialState);
    case ISSUE_CREATE_API_FULFILLED:
      return state.merge({
        limit: state.limit + 1,
        totalResults: state.totalResults + 1
      });
    default:
      return state;
  }
};

const issuesCollectionSearchResultsReducer = (state = null, action) => {
  switch (action.type) {
    case ISSUES_FETCH_SEARCH_API_FULFILLED:
      return action.payload;
    case ISSUES_CLEAR_SEARCH:
      return [];
    default:
      return state;
  }
};

export default (state = {}, action) => ({
  // Is the batch-fetch for entire collection still loading?
  batchLoading: issuesBatchLoadingReducer(state.batchLoading, action),
  // Is the the single, one-at-a-time, sub-batch still loading?
  loading: issuesLoadingReducer(state.loading, action),
  didFetchOnce: issuesFetchedOnceReducer(state.didFetchOnce, action),
  error: issuesErrorReducer(state.error, action),
  lastUpdatedKey: lastUpdatedKeyReducer(state.lastUpdatedKey, action),
  lastQueryKey: issuesLastQueryKeyReducer(state.lastQueryKey, action),
  sortOrder: issuesSortOrderReducer(state.sortOrder, action),
  collection: issuesCollectionReducer(state.collection, action),
  pagination: issuesCollectionPaginationReducer(state.pagination, action),
  searchResults: issuesCollectionSearchResultsReducer(state.searchResults, action)
});
